/*
 * Shop the look — drawer cards and "add all to cart" button (snippets/product-shop-the-look.liquid).
 *
 * <shop-the-look-card> wraps a product-cross-sell card that carries its own variant picker (the theme's option values).
 * Picking an option resolves the variant from the card's variant JSON, without a network round-trip, and updates what
 * the card adds (form id + "variant-id" attribute), its image, price, link, sold-out state and the unavailable values.
 *
 * Mirrors the theme's own ProductForm submit (assets/theme.js): collect the sections to bundle, POST /cart/add.js, then
 * dispatch "variant:add" + "cart:change" so the cart drawer re-renders and opens exactly as after a normal add to cart.
 * Only the documented theme events are used (docs/theme-docs/javascript-events.md).
 */
class ShopTheLookCard extends HTMLElement {
  connectedCallback() {
    const data = JSON.parse(this.querySelector('script[data-shop-the-look-variants]')?.textContent || '{}');

    this.optionValues = data.option_values || {};
    this.variants = data.variants || [];

    if (this.variants.length < 2) {
      return;
    }

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[data-option-position]')) {
        this.onOptionChange(event.target);
      }
    });

    this.refreshUnavailableValues();
  }

  get optionInputs() {
    return Array.from(this.querySelectorAll('input[data-option-position]'));
  }

  /* The selected option value names, indexed by option position - 1 */
  get selectedOptions() {
    const selected = [];
    this.optionInputs.filter((input) => input.checked).forEach((input) => {
      selected[parseInt(input.dataset.optionPosition) - 1] = this.optionValues[input.value];
    });
    return selected;
  }

  onOptionChange(input) {
    const position = parseInt(input.dataset.optionPosition) - 1;
    const selected = this.selectedOptions;
    let variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));

    // The combination does not exist: keep the value just picked and fall back to the closest variant carrying it
    if (!variant) {
      const withValue = this.variants.filter((candidate) => candidate.options[position] === selected[position]);
      variant = withValue.find((candidate) => candidate.available) || withValue[0];
    }

    if (variant) {
      this.selectVariant(variant);
    }
  }

  selectVariant(variant) {
    variant.options.forEach((value, index) => {
      const input = this.optionInputs.find((item) => parseInt(item.dataset.optionPosition) === index + 1 && this.optionValues[item.value] === value);

      if (input) {
        input.checked = true;
      }
    });

    this.setAttribute('variant-id', variant.id);
    this.toggleAttribute('available', variant.available);

    const form = this.querySelector('form');

    if (form?.elements.id) {
      form.elements.id.value = variant.id;
    }

    this.querySelector('product-form')?.toggleAttribute('hidden', !variant.available);
    this.querySelector('.product-cross-sell__sold-out')?.toggleAttribute('hidden', variant.available);
    this.querySelectorAll('a[href]').forEach((link) => link.href = variant.url);

    const image = this.querySelector('a > img');

    if (image && variant.image) {
      image.src = variant.image.src;
      image.srcset = variant.image.srcset;
    }

    // Add button « Ajouter — 65 € » (drawer): the visible text and the hover animation's copy of it
    const buttonContent = this.querySelector('product-form button-content');
    const addLabel = this.getAttribute('add-label');

    if (buttonContent && addLabel && variant.price_label) {
      const text = `${addLabel} — ${variant.price_label}`;
      buttonContent.textContent = text;
      ['up-text', 'rotated-text'].forEach((name) => buttonContent.hasAttribute(name) && buttonContent.setAttribute(name, text));
    }

    const price = this.querySelector('[data-card-price]');
    const priceTemplate = this.querySelector(`template[data-price="${variant.id}"]`);

    if (price && priceTemplate) {
      price.replaceChildren(priceTemplate.content.cloneNode(true));
    }

    this.refreshUnavailableValues();
    this.dispatchEvent(new CustomEvent('shop-the-look:variant-change', { bubbles: true, detail: { variant } }));
  }

  /* Cross out the values that, combined with the other selected options, give no available variant */
  refreshUnavailableValues() {
    const selected = this.selectedOptions;

    this.optionInputs.forEach((input) => {
      const position = parseInt(input.dataset.optionPosition) - 1;
      const value = this.optionValues[input.value];
      const available = this.variants.some((candidate) => candidate.available && candidate.options[position] === value && candidate.options.every((other, index) => index === position || other === selected[index]));

      this.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.toggle('is-disabled', !available);
    });
  }
}

if (!window.customElements.get('shop-the-look-card')) {
  window.customElements.define('shop-the-look-card', ShopTheLookCard);
}

class ShopTheLookAddAll extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.error = this.querySelector('[role="alert"]');
    this.button?.addEventListener('click', () => this.addAll());
    this.closest('x-drawer')?.addEventListener('shop-the-look:variant-change', () => this.button.disabled = this.variantIds.length === 0);
  }

  /* Each card's current selection when it is available; the ids rendered in Liquid otherwise */
  get variantIds() {
    const cards = Array.from(this.closest('x-drawer')?.querySelectorAll('shop-the-look-card') || []);

    if (cards.length) {
      return cards.filter((card) => card.hasAttribute('available')).map((card) => card.getAttribute('variant-id'));
    }

    return (this.getAttribute('variant-ids') || '').split(',').filter(Boolean);
  }

  /* "_shop_the_look" is hidden in cart and checkout (leading underscore) and kept on the order, to measure the drawer's sales */
  lineItem(id) {
    const item = { id: Number(id), quantity: 1 };
    const source = this.getAttribute('look-source');

    if (source) {
      item.properties = { _shop_the_look: source };
    }

    return item;
  }

  async addAll() {
    const ids = this.variantIds;

    if (!ids.length || this.button.getAttribute('aria-busy') === 'true') {
      return;
    }

    this.button.setAttribute('aria-busy', 'true');
    this.error.hidden = true;

    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: ids.map((id) => this.lineItem(id)), sections: sections.join(',') })
      });
      const responseJson = await response.json();

      if (!response.ok) {
        this.error.textContent = responseJson.description || responseJson.message;
        this.error.hidden = false;
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description || responseJson.message } }));
        return;
      }

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart['sections'] = responseJson['sections'];

      this.closest('x-drawer')?.hide();
      this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: responseJson['items'], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } finally {
      this.button.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('shop-the-look-add-all')) {
  window.customElements.define('shop-the-look-add-all', ShopTheLookAddAll);
}
